// UserProfile.js
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import API_URL from "../config/config";
import {
  Box,
  Main,
  Header,
  Heading,
  Text,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Avatar,
  Spinner,
  Button,
  Grid,
  Anchor,
  CheckBox,
  Notification,
} from "grommet";
import { HeaderNavigation } from "../components/Headers/GlobalHeader";
import { StickyFooter } from "../components/Footers/GlobalFooter";
import adminAxios from "../config/adminAxios";

const getInitials = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0]?.toUpperCase())
    .join("") || "U";

const InfoRow = ({ label, value }) => (
  <Box direction="row" gap="small" pad={{ vertical: "xsmall" }}>
    <Text weight="bold" color="dark-3" style={{ minWidth: 160 }}>
      {label}:
    </Text>
    <Text>{value ?? "Not provided"}</Text>
  </Box>
);

const Skeleton = () => (
  <Card elevation="small" round="large" background="white" width="large">
    <CardBody pad="medium" gap="medium">
      <Box direction="row" gap="medium" align="center">
        <Box width="80px" height="80px" round="full" background="light-3" animation="pulse" />
        <Box gap="xsmall" flex>
          <Box height="24px" width="40%" background="light-3" round="xsmall" animation="pulse" />
          <Box height="16px" width="60%" background="light-3" round="xsmall" animation="pulse" />
        </Box>
      </Box>
      <Box gap="small">
        {[1, 2, 3].map((i) => (
          <Box key={i} height="16px" background="light-3" round="xsmall" animation="pulse" />
        ))}
      </Box>
    </CardBody>
  </Card>
);

const toBool = (v) => v === 1 || v === "1" || v === true;
const toNum = (b) => (b ? 1 : 0);

const UserProfile = () => {
  const { userid } = useParams();
  const [profile, setProfile] = useState(null);
  const [state, setState] = useState({ loading: true, error: null });

  // local UI state for the toggle + save status
  const [agree, setAgree] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null); // {status, message}

  const pageTitle = useMemo(
    () => (profile?.name ? `${profile.name} — profile` : "Profile details"),
    [profile?.name]
  );

  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  useEffect(() => {
    if (!userid) return;

    const controller = new AbortController();
    const run = async () => {
      setState({ loading: true, error: null });
      try {
        const res = await adminAxios.get(`${API_URL}/user/getUser/${userid}`, {
          signal: controller.signal,
        });
        const data = res.data;
        const user = data?.user ?? null;
        setProfile(user);
        setAgree(toBool(user?.agree_to_communicate));
        setState({ loading: false, error: null });
      } catch (err) {
        if (err.name === "AbortError") return;
        setState({ loading: false, error: err.message || "Failed to fetch user profile." });
      }
    };
    run();
    return () => controller.abort();
  }, [userid]);

  const retry = () => {
    setNotice(null);
    setState((s) => ({ ...s, loading: true, error: null }));
    setProfile(null);
  };

  // -- PATCH agree_to_communicate (optimistic update)
  const toggleAgree = async (nextValue) => {
    if (!profile) return;
    setSaving(true);
    setAgree(nextValue); // optimistic
    setNotice(null);

    try {
      const res = await adminAxios.patch(`${API_URL}/user/getUser/${userid}`, {
        agree_to_communicate: toNum(nextValue),
      });

      // If backend returns the updated user, sync it. If not, just patch locally.
      const maybe = res.data ?? null;
      const updatedUser = maybe?.user
        ? maybe.user
        : { ...profile, agree_to_communicate: toNum(nextValue) };

      setProfile(updatedUser);
      setAgree(toBool(updatedUser.agree_to_communicate));
      setNotice({ status: "normal", message: nextValue ? "You have agreed to be contacted by HPE representative regarding important communications." : "You have opted out to be contacted by HPE representative (you’ll still receive critical service emails if applicable)." });
    } catch (err) {
      // rollback
      setAgree(toBool(profile.agree_to_communicate));
      setNotice({ status: "critical", message: err.message || "Could not save your preference." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box fill="vertical" justify="between" background="light-1">
      {/* <Box flex="grow"> */}
      <HeaderNavigation />
      <Main align="center" pad={{ horizontal: "medium", top: "small" }}>
        <Header
          pad={{ vertical: "small" }}
          justify="between"
          width="xlarge"
          alignSelf="center"
          border={{ side: "bottom", color: "light-4" }}
        >
          <Heading level={2} margin="none">
            Your information and preferences
          </Heading>
          {/* {profile?.email && <Anchor href={`mailto:${profile.email}`} label="Contact" />} */}
        </Header>

        <Box width="xlarge" pad={{ vertical: "small" }}>
          {state.loading && (
            <Box align="center" gap="medium" pad="medium">
              <Spinner />
              <Skeleton />
            </Box>
          )}

          {!state.loading && state.error && (
            <Card elevation="small" round="large" background="status-error" width="large">
              <CardBody pad="medium" gap="small">
                <Heading level={4} margin="none" color="white">
                  Something went wrong
                </Heading>
                <Text color="white">{state.error}</Text>
              </CardBody>
              <CardFooter pad={{ horizontal: "medium", vertical: "small" }} background="rgba(255,255,255,0.1)">
                <Button onClick={retry} label="Retry" primary />
              </CardFooter>
            </Card>
          )}

          {!state.loading && !state.error && !profile && (
            <Card elevation="small" round="large" background="white" width="large">
              <CardBody pad="medium" gap="small">
                <Heading level={4} margin="none">
                  User not found
                </Heading>
                <Text>We couldn’t find a profile for ID: {userid}</Text>
              </CardBody>
            </Card>
          )}

          {notice && (
            <Box margin={{ bottom: "small" }} width="xlarge">
              <Notification
                status={notice.status} // "normal" | "critical" | "warning" | "unknown"
                message={notice.message}
                onClose={() => setNotice(null)}
              />
            </Box>
          )}
          {!state.loading && !state.error && profile && (
            <Card elevation="small" round="large" background="white" width="xlarge">
              <CardHeader pad={{ horizontal: "medium", vertical: "small" }}>
                <Box direction="row" gap="medium" align="center">
                  <Avatar background="brand" size="large" title={profile.name} src={profile.avatarUrl || undefined}>
                    {!profile.avatarUrl ? getInitials(profile.name) : null}
                  </Avatar>
                  <Box>
                    <Heading level={3} margin="xsmall">
                      {profile.name || "Unnamed User"}
                    </Heading>
                    <Text color="dark-5">{profile.email || "Email not provided"}</Text>
                  </Box>
                </Box>
              </CardHeader>

              <CardBody pad={{ horizontal: "medium", vertical: "small" }} gap="medium">
                {/* Read-only details */}
                <Grid columns={{ count: "fit", size: ["auto", "flex"] }} gap="medium">
                  <Box>
                    <Heading level={4} margin={{ bottom: "small", top: "none" }}>
                      Details
                    </Heading>
                    <InfoRow label="Organization" value={profile.organization} />
                    <InfoRow label="Role" value={profile.role} />
                    <InfoRow label="Country" value={profile.country} />
                    <InfoRow label="Phone" value={profile.phone} />
                  </Box>

                  {/* <Box>
                      <Heading level={4} margin={{ bottom: "small", top: "none" }}>
                        About
                      </Heading>
                      <Text>
                        {profile.bio ||
                          "No bio added yet. Share a short summary about yourself, your team, and what you’re working on."}
                      </Text>
                    </Box> */}
                </Grid>

                {/* Editable ONLY: agree_to_communicate */}
                <Box pad={{ vertical: "small" }} border={{ side: "top", color: "light-4" }}>
                  <Heading level={4} margin={{ top: "small", bottom: "xsmall" }}>
                    Communication preferences
                  </Heading>
                  <Text size="small" color="dark-5" margin={{ bottom: "small" }}>
                    Control whether we can contact you for important updates and marketing. You can change this anytime.
                  </Text>

                  <CheckBox
                    toggle
                    label={
                      <Box>
                        <Text weight="bold">
                          Consent to be contacted by an HPE representative
                        </Text>
                        <Text size="small" color="dark-5">
                          {agree
                            ? "Enabled — Opted in to be contacted by an HPE representative."
                            : "Disabled — Opted out to be contacted by an HPE representative; critical service emails may still be sent if required."}
                        </Text>
                      </Box>
                    }
                    checked={agree}
                    disabled={saving}
                    onChange={(e) => toggleAgree(e.target.checked)}
                  />
                  {saving && (
                    <Box direction="row" align="center" gap="small" margin={{ top: "small" }}>
                      <Spinner size="xsmall" />
                      <Text size="small" color="dark-5">Saving your preference…</Text>
                    </Box>
                  )}
                </Box>
              </CardBody>

              <CardFooter pad={{ horizontal: "medium", vertical: "small" }} background="light-2" justify="between">
                <Text size="small" color="dark-5">
                  User ID: {userid}
                </Text>
                <Box direction="row" gap="small">
                  {/* <Button label="Refresh" onClick={retry} secondary /> */}
                </Box>
              </CardFooter>
            </Card>
          )}

        </Box>
      </Main>
      <StickyFooter />
      {/* </Box> */}
    </Box>
  );
};

export default UserProfile;
